import { Block } from "baseui/block";
import { styled, useStyletron } from "baseui";
import {
  useGetStripeSubscriptionByIdQuery,
  useGetSubscriptionOwnerQuery,
} from "../../services/resourceGroups/subscriptionApi";
import { ListItem, ListItemLabel } from "baseui/list";
import { useGetResourceGroupsQuery } from "../../services/resourceGroups/resourceGroupApi";
import { StyledLink } from "baseui/link";
import { Button } from "baseui/button";
import { ChevronRight, Plus } from "baseui/icon";
import { useGetTenantsQuery } from "../../services/resourceGroups/tenantApi";
import { VscKey, VscTrash } from "react-icons/vsc";
import { CreateSubscriptionDialog, DeleteSubscriptionDialog } from "../subscription";
import { useMemo, useState } from "react";
import { BsStripe } from "react-icons/bs";
import { Input } from "baseui/input";
import { getStripeId } from "../../services/resourceGroups/utils";
import { Badge, COLOR, HIERARCHY, SHAPE } from "baseui/badge";
import SubscriptionPaymentDetails from "./SubscriptionPaymentDetails";
import SubscriptionStatusView from "../subscription/SubscriptionStatus";
import { useIntl } from "react-intl";
import { getStripeUrl } from "../../utils/utils";
import { AccessRoles, useHasRole } from "../../hooks/useHasRole";
import { ISubscription, SubscriptionStatus } from "@oqdo/automationapps/Memberships/Subscription";
import { LoadingView } from "../../components/LoadingView";
import { Avatar } from "baseui/avatar";
import { usePagedSubscriptionsQuery } from "../../hooks/usePagedSubscriptionsQuery";
import { LoadNextPageButton } from "../../components/LoadNextPageButton";
import RenderIfVisible from "react-render-if-visible";

const Subscriptions = () => {
  const intl = useIntl();
  const [selected, setSelected] = useState<number>(3);
  const [css] = useStyletron();
  const [search, setSearch] = useState<any>();
  const isSa = useHasRole(AccessRoles.SuperAdmin);

  const { subscriptions, isSuccess, isLoading, page, setPage, hasMore, total, isFetching } =
    usePagedSubscriptionsQuery(100);
  const data = subscriptions;
  const notManyItems = data && data.length < 10;
  const items = useMemo(() => {
    const sorted = data?.slice().sort((a, b) => getSortValue(a, b));
    if (notManyItems) {
      if (search && search !== "")
        return sorted?.filter(
          (v) =>
            v.name.toLowerCase().includes(search.toLowerCase()) ||
            v.description?.toLowerCase().includes(search.toLowerCase())
        );
      return sorted;
    }

    const filteredByState = selected >= 0 ? sorted?.filter((v) => v.status === selected) : sorted;
    if (search && search !== "")
      return filteredByState?.filter(
        (v) =>
          v.name.toLowerCase().includes(search.toLowerCase()) ||
          v.description?.toLowerCase().includes(search.toLowerCase())
      );
    return filteredByState;
  }, [data, search, selected]);

  const states = Object.keys(SubscriptionStatus).filter((item) => {
    return isNaN(Number(item));
  });
  const stateNames = states
    .map((item: any, index: number) => {
      return {
        label: intl.formatMessage({
          id: `StripeSubscriptionStatus.${item}`,
          defaultMessage: `${item}`,
        }),
        id: index,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <MainContainer>
      <LoadingView isLoading={isLoading} isSucess={isSuccess}>
        <MainContentContainer>
          {!notManyItems && (
            <StatesContainer>
              {stateNames.map((v) => {
                const numItems = countPerState(v.id, data);
                return (
                  <Button
                    key={v.id}
                    endEnhancer={
                      <>{numItems ? <Badge color={COLOR.primary} hierarchy="secondary" content={numItems} /> : <></>}</>
                    }
                    size="compact"
                    onClick={() => {
                      setSelected(v.id);
                    }}
                    isSelected={selected === v.id}
                    kind="tertiary"
                    overrides={{
                      BaseButton: {
                        style: ({ $theme, $isSelected }) => ({
                          color: $isSelected ? $theme.colors.accent : undefined,
                          outline: $isSelected
                            ? `1px solid` + $theme.colors.borderAccent
                            : `1px solid` + $theme.colors.inputBorder,
                          background: "transparent",
                        }),
                      },
                    }}>
                    {v.label}
                  </Button>
                );
              })}
            </StatesContainer>
          )}

          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search ..."
            clearOnEscape
          />
          <ul
            className={css({
              //height: "75vh",
              paddingLeft: 0,
              paddingRight: 0,
              overflowY: "auto",
            })}>
            {items?.map((v, i) => (
              <RenderIfVisible
                initialVisible={i < 15}
                defaultHeight={listItemHeight}
                visibleOffset={100}
                stayRendered
                key={v.id}>
                <SubscriptionListItem {...v} />
              </RenderIfVisible>
            ))}
          </ul>
        </MainContentContainer>
        {isSa && <NewSubscription />}
        <LoadNextPageButton hasMore={hasMore} isFetching={isFetching} page={page} total={total} setPage={setPage} />
      </LoadingView>
    </MainContainer>
  );
};

export default Subscriptions;

const listItemHeight = 50;

const SubscriptionListItem: React.FC<ISubscription> = (props) => {
  const item = props;
  return <SubscriptionItem key={item.id} subscription={item} />;
};

const countPerState = (state: SubscriptionStatus, subs: ISubscription[] | undefined) =>
  subs?.filter((v) => v.status === state).length ?? 0;

const getSortValue = (a, b): number => {
  const aval = a.description && a.description !== "" ? a.description : a.name;
  const bval = b.description && b.description !== "" ? b.description : b.name;
  return aval.toLowerCase().localeCompare(bval.toLowerCase());
};

const SubscriptionItem = (props: { subscription: ISubscription }) => {
  const [, theme] = useStyletron();

  const item = props.subscription;
  const stripeId = getStripeId(item);
  const { data: owner, isSuccess } = useGetSubscriptionOwnerQuery(props.subscription.id);
  const { data: stripeSub, isError: stripeSubIsError } = useGetStripeSubscriptionByIdQuery(stripeId, {
    skip: !stripeId,
  });

  const isCanceled = item?.status === SubscriptionStatus.canceled;
  return (
    <ListItem
      shape="ROUND"
      overrides={{
        Root: {
          style: ({ $theme }) => ({
            backgroundColor: isCanceled ? $theme.colors.backgroundSecondary : undefined,
            opacity: isCanceled ? 0.5 : undefined,
            filter: isCanceled ? "grayscale(100%)" : undefined,
          }),
        },
      }}
      artwork={() => (
        <>
          {owner ? (
            <Avatar
              name={owner.name !== "" ? owner.name : "no Owner"}
              overrides={{
                Avatar: {
                  style: ({ $theme }) => ({
                    height: "auto",
                  }),
                },
              }}
            />
          ) : (
            <VscKey color={theme.colors.primary} />
          )}
        </>
      )}
      key={item.id}
      endEnhancer={() => (
        <Block gridGap={theme.sizing.scale400} display={"flex"} alignItems={"baseline"}>
          {isSuccess && !owner && <Badge content={"Now owner"} hierarchy="secondary" color="warning" shape="pill" />}

          {stripeSub && !stripeSubIsError && (
            <>
              <BsStripe color={"#625afa"} />
              <StyledLink target="_blank" href={getStripeUrl(stripeSub.id)}>
                Stripe
              </StyledLink>
            </>
          )}
          {!stripeSub && !stripeSubIsError && (
            <Badge content={"Not paid"} hierarchy={HIERARCHY.primary} shape={SHAPE.pill} color={COLOR.negative} />
          )}

          <Button $as="a" href={`/subscriptions/${item.id}`} kind="tertiary" shape="circle">
            <ChevronRight size="scale800" />
          </Button>
        </Block>
      )}>
      <ListItemLabel description={<SubscriptionDescription subscription={item} />}>
        <SubscriptionNameContainer>
          {item.name}
          <SubscriptionStatusView {...item} />
        </SubscriptionNameContainer>
      </ListItemLabel>
    </ListItem>
  );
};

const NewSubscription = () => {
  const [css, theme] = useStyletron();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button
        onClick={() => {
          setIsOpen(true);
        }}
        className={css({
          position: "fixed",
          bottom: "100px",
          right: "100px",
          boxShadow: theme.lighting.shadow500,
        })}
        title="Add Subscription"
        size="default"
        kind="primary"
        shape="circle">
        <Plus title="Add Subscription" />
      </Button>
      {isOpen && <CreateSubscriptionDialog isOpen={isOpen} setIsOpen={setIsOpen} />}
    </>
  );
};

const DeleteSubscription = ({ subscription }) => {
  const [isOpen, setIsOpen] = useState(false);
  const s: ISubscription = subscription;
  const { data: rgsData } = useGetResourceGroupsQuery({ subscriptionId: s.id, page: 0, pageSize: 1000 });
  const { data: tenantsData } = useGetTenantsQuery(s.id);
  const rgs = rgsData?.data;
  const tenants = tenantsData?.data;

  const hasRgs = rgs && rgs.length > 0;
  const hasTs = tenants && tenants.length > 0;
  return (
    <>
      <Button
        disabled={hasRgs || hasTs}
        onClick={() => {
          setIsOpen(true);
        }}
        title="Delete Subscription"
        size="default"
        kind="secondary"
        shape="circle">
        <VscTrash />
      </Button>
      <DeleteSubscriptionDialog subscription={subscription} isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
};

const SubscriptionDescription = (props: { subscription: ISubscription }) => {
  const [css, theme] = useStyletron();
  return <SubscriptionPaymentDetails subscription={props.subscription} />;
};

const SubscriptionNameContainer = styled("div", ({ $theme }) => ({
  display: "flex",
  gap: $theme.sizing.scale200,
}));

const MainContentContainer = styled("div", ({ $theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: $theme.sizing.scale400,
}));

const StatesContainer = styled("div", ({ $theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: $theme.sizing.scale400,
}));

const MainContainer = styled("div", ({ $theme }) => ({
  width: "100%",
  height: "80vh",
  position: "relative",
}));
